import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

export function formatCNPJ(cnpj: string): string {
  const cleaned = cnpj.replace(/\D/g, '')
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  )
}

export function formatCPF(cpf: string): string {
  return cpf.replace(/\D/g, '').replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
}

export function formatCustomerDocument(customer: { cpf?: string | null; cnpj?: string | null }): string {
  return customer.cpf ? formatCPF(customer.cpf) : customer.cnpj ? formatCNPJ(customer.cnpj) : '—'
}

export function formatPhone(phone: string): string {
  const cleaned = phone.replace(/\D/g, '')
  if (cleaned.length === 11) {
    return cleaned.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3')
  }
  return cleaned.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3')
}

/**
 * Datas sem hora (payment_due_date = 'YYYY-MM-DD') sao interpretadas como dia
 * local; `new Date('2026-09-10')` seria UTC e apareceria como 09/09 no Brasil.
 */
export function formatDate(date: string | Date): string {
  const d =
    typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
      ? new Date(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)))
      : new Date(date)
  return new Intl.DateTimeFormat('pt-BR').format(d)
}

/**
 * Link de rastreio universal da Maxfem: aceita o codigo no caminho e detecta a
 * transportadora sozinho (J&T, Loggi, FM Transportes, Correios). Nao montar link
 * por transportadora. Vazio fica sem link.
 */
export function trackingUrl(code: string | null | undefined): string | null {
  const c = (code || '').trim()
  return c ? `https://rastreio.maxfem.com.br/${encodeURIComponent(c)}` : null
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(date))
}

export function validateCNPJ(cnpj: string): boolean {
  const cleaned = cnpj.replace(/\D/g, '')

  if (cleaned.length !== 14) return false
  if (/^(\d)\1+$/.test(cleaned)) return false

  let sum = 0
  let weight = 5
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleaned[i]) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  let digit = 11 - (sum % 11)
  if (digit > 9) digit = 0
  if (parseInt(cleaned[12]) !== digit) return false

  sum = 0
  weight = 6
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleaned[i]) * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  digit = 11 - (sum % 11)
  if (digit > 9) digit = 0
  if (parseInt(cleaned[13]) !== digit) return false

  return true
}

export const ORDER_STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  pending_payment: 'Aguardando Pagamento',
  approved: 'Aprovado a prazo',
  paid: 'Aguardando envio',
  invoiced: 'Faturado',
  shipped: 'Enviado',
  delivered: 'Entregue',
  cancelled: 'Cancelado',
}

export const ORDER_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_payment: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-teal-100 text-teal-800',
  paid: 'bg-amber-100 text-amber-800',
  invoiced: 'bg-blue-100 text-blue-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-800',
}

/**
 * Prazos de pagamento do cliente (b2b_customers.payment_terms). Mesma lista
 * fechada de b2b_payment_terms_allowed() no banco: dias separados por "/" e
 * sufixo "ddl". Com mais de um dia, sai um boleto por parcela.
 */
export const PAYMENT_TERMS_OPTIONS: { value: string; label: string }[] = [
  { value: 'antecipado', label: 'Antecipado' },
  { value: '7ddl', label: '7 dias' },
  { value: '14ddl', label: '14 dias' },
  { value: '21ddl', label: '21 dias' },
  { value: '28ddl', label: '28 dias' },
  { value: '30ddl', label: '30 dias' },
  { value: '45ddl', label: '45 dias' },
  { value: '60ddl', label: '60 dias' },
  { value: '30/60ddl', label: '30/60 dias (2 boletos)' },
  { value: '30/45/60ddl', label: '30/45/60 dias (3 boletos)' },
  { value: '30/60/90ddl', label: '30/60/90 dias (3 boletos)' },
]

/** Dias de cada parcela, como b2b_payment_terms_days(): '30/45/60ddl' vira [30, 45, 60]; 'antecipado' vira []. */
export function paymentTermsDays(terms?: string | null): number[] {
  return (terms || '')
    .split('/')
    .map((token) => token.replace(/ddl$/i, '').trim())
    .filter((token) => /^\d+$/.test(token))
    .map(Number)
    .filter((days) => days > 0)
}

/** Rótulo por extenso, como b2b_payment_terms_label(): 'Antecipado', '28 dias', '30/45/60 dias'. */
export function paymentTermsLabel(terms?: string | null): string {
  const days = paymentTermsDays(terms)
  return days.length ? `${days.join('/')} dias` : 'Antecipado'
}

/** Prazo que dá direito a boleto faturado (algum dia de prazo). */
export function isCreditTerms(terms?: string | null): boolean {
  return paymentTermsDays(terms).length > 0
}

function round2(value: number): number {
  return Math.round(value * 100 + 1e-9) / 100
}

/**
 * Valor de cada parcela, como b2b_release_credit_order(): round(total/n, 2) e
 * a diferença de centavos fica na última parcela.
 */
export function splitInstallments(total: number, count: number): number[] {
  if (!Number.isFinite(total) || count < 1) return []
  const each = round2(total / count)
  return Array.from({ length: count }, (_, index) =>
    index === count - 1 ? round2(total - each * (count - 1)) : each)
}

export const CUSTOMER_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  blocked: 'Bloqueado',
}

export const CUSTOMER_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  blocked: 'bg-gray-100 text-gray-800',
}
